// Logic màn tra P2P, chép từ script board Mint-P2P (khối "GIÁ"):
// giá hết hạn không vào danh sách mà gom theo hãng (QĐ 123) · sắp xếp Giá / T/T / Hiệu lực · dòng rẻ nhất theo cỡ cont lớn nhất.
import { CLASS_CONTAINERS, cheapestIndex, daysBetween, rateValidity, type CargoType, type RateValidity } from '@hana/domain';
import type { P2PRate, Sailing } from './types';

export type RateSort = 'PRICE' | 'TT' | 'VALID';

export interface RateQuery {
  pol: string;
  pod: string;
  cargo: CargoType;
  /** `YYYY-MM-DD` */
  today: string;
  carrier?: string | null;
  sort?: RateSort;
  /** Loại cont đang chọn (`20GP`, `40HC`…): quyết định cột giá dùng để sắp xếp và chọn dòng rẻ nhất. Mặc định cỡ lớn nhất của loại hàng. */
  container?: string;
  /** Bậc giá bán đang xem (board: Bậc 1 / Bậc 2, mặc định Bậc 2). */
  tier?: SellTier;
}

export interface RateHit {
  rate: P2PRate & { validEnd: string };
  validity: RateValidity;
  daysLeft: number;
  cheapest: boolean;
}

export interface RateResult {
  hits: RateHit[];
  /** Hãng có giá còn hiệu lực (chip lọc hãng). */
  carriers: string[];
  /** Giá hết hạn gom theo hãng, cho tab Lịch sử giá. */
  expired: Array<{ carrier: string; rates: P2PRate[] }>;
}

export type SellTier = 1 | 2;

/** Giá bán theo loại cont (board: 20' lấy cột 20, còn lại cột 40) và bậc (mặc định Bậc 2). Chưa gồm local charge. */
export function sellOf(rate: P2PRate, container: string, tier: SellTier = 2): number {
  const is20 = container.startsWith('20');
  if (tier === 1) return (is20 ? rate.tier1Sell20 : rate.tier1Sell40) ?? (is20 ? rate.sell20 : rate.sell40);
  return is20 ? rate.sell20 : rate.sell40;
}

export function searchRates(rates: readonly P2PRate[], q: RateQuery): RateResult {
  // Dòng thiếu ValidEnd không đánh giá được hiệu lực → bỏ qua, không làm hỏng cả lần tra.
  const lane = rates
    .filter((r): r is P2PRate & { validEnd: string } => !!r.validEnd && r.pol === q.pol && r.pod === q.pod && r.cargo === q.cargo)
    .map((rate) => ({ rate, ...rateValidity({ validFrom: rate.validFrom, validEnd: rate.validEnd }, q.today) }));
  // Giá chưa hiệu lực chưa vào danh sách.
  const live = lane.filter((h) => h.status !== 'expired' && h.status !== 'upcoming');
  const carriers = [...new Set(live.map((h) => h.rate.carrier))];
  const shown = live.filter((h) => !q.carrier || h.rate.carrier === q.carrier);

  const sizes = CLASS_CONTAINERS[q.cargo];
  const container = q.container ?? sizes[sizes.length - 1]!;
  type Row = (typeof shown)[number];
  const byPrice = (a: Row, b: Row) => sellOf(a.rate, container, tier) - sellOf(b.rate, container, tier);
  const tier = q.tier ?? 2;
  const sort = q.sort ?? 'PRICE';
  const sorter =
    sort === 'TT'
      ? (a: Row, b: Row) => a.rate.transitDays - b.rate.transitDays || byPrice(a, b)
      : sort === 'VALID'
        ? (a: Row, b: Row) => b.daysLeft - a.daysLeft || byPrice(a, b)
        : byPrice;

  // "Rẻ nhất" so trên mọi hãng còn hạn của tuyến, không chỉ hãng đang lọc.
  const cheapestAt = cheapestIndex(live.map((h) => sellOf(h.rate, container, tier)));
  const best = cheapestAt < 0 ? null : live[cheapestAt]!.rate;

  const expired = new Map<string, P2PRate[]>();
  for (const h of lane) if (h.status === 'expired') expired.set(h.rate.carrier, [...(expired.get(h.rate.carrier) ?? []), h.rate]);

  return {
    hits: [...shown].sort(sorter).map((h) => ({ rate: h.rate, validity: h.status, daysLeft: h.daysLeft, cheapest: h.rate === best })),
    carriers,
    expired: [...expired].map(([carrier, list]) => ({ carrier, rates: list.sort((a, b) => (b.validEnd ?? '').localeCompare(a.validEnd ?? '')) })),
  };
}

export interface SailingQuery {
  pol: string;
  pod: string;
  /** `YYYY-MM-DD` */
  today: string;
  /** Cửa sổ ETD tính từ hôm nay (LOGI: 14 ngày tới). */
  days?: number;
  carrier?: string | null;
}

/** Chuyến theo tuyến trong cửa sổ ETD, sắp theo ETD rồi T/T. */
export function searchSailings(sailings: readonly Sailing[], q: SailingQuery): Sailing[] {
  const days = q.days ?? 14;
  return sailings
    .filter((s) => s.pol === q.pol && s.pod === q.pod && (!q.carrier || s.carrier === q.carrier))
    .filter((s) => {
      const d = daysBetween(q.today, s.etd);
      return d >= 0 && d <= days;
    })
    .sort((a, b) => a.etd.localeCompare(b.etd) || a.transitDays - b.transitDays);
}

// ---------- Free time (QĐ 127: tách DEM / DET / cắm điện) ----------

export interface FreetimePart {
  kind: 'COMBINED' | 'DEM' | 'DET' | 'PLUG';
  days: number;
  /** Phạm vi ghi trong ngoặc, vd DRY / RF */
  scope?: string;
}

/** Đọc chuỗi free time tự do của LOGI ("5 Dem / 5 Det / 3 Plug", "14 days combined", "14 DEM (DRY), 10 DEM (RF)"). */
export function parseFreetime(text: string): { parts: FreetimePart[]; caseByCase: boolean } {
  const parts: FreetimePart[] = [];
  const re = /(\d+)\s*(days?\s*combined|dem|det|plug)(?:\s*\((\w+)\))?/gi;
  for (const m of text.matchAll(re)) {
    const w = m[2]!.toLowerCase();
    const part: FreetimePart = { kind: w.startsWith('day') ? 'COMBINED' : (w.toUpperCase() as 'DEM' | 'DET' | 'PLUG'), days: Number(m[1]) };
    if (m[3]) part.scope = m[3].toUpperCase();
    parts.push(part);
  }
  return { parts, caseByCase: !parts.length && /case/i.test(text) };
}

/** Nhãn ngắn như thẻ giá của board. */
export function freetimeLabel(text: string): string {
  const { parts, caseByCase } = parseFreetime(text);
  if (!parts.length) return caseByCase ? 'theo từng lô' : text.trim() || '—';
  return parts
    .map((p) => {
      const scope = p.scope ? ` (${p.scope})` : '';
      if (p.kind === 'COMBINED') return `${p.days} ngày gộp DEM + DET`;
      return `${p.kind === 'PLUG' ? 'Cắm điện' : p.kind} ${p.days}${scope}`;
    })
    .join(' · ');
}
